<template>
  <div class="console-page report-view">
    <h1 class="console-page__title">Event-wise users</h1>
    <p class="console-page__subtitle">
      Daily user counts per event. Pick a date range to load the matrix, then
      export when you need a spreadsheet.
    </p>

    <a-card :bordered="false" class="console-card console-card--toolbar">
      <div class="report-view__toolbar">
        <div class="report-view__toolbar-main">
          <div class="report-view__toolbar-label">
            <a-icon type="calendar" />
            <span>Date range</span>
          </div>
          <a-range-picker
            v-model="dateRange"
            format="YYYY-MM-DD"
            :allow-clear="true"
            class="report-view__range-picker"
            @change="handleDateChange"
          />
          <div class="report-view__presets">
            <a-button size="small" @click="applyPreset(7)">Last 7 days</a-button>
            <a-button size="small" @click="applyPreset(30)">Last 30 days</a-button>
          </div>
        </div>
        <a-button
          type="primary"
          icon="download"
          :disabled="!canExport"
          @click="downloadExcel"
        >
          Download Excel
        </a-button>
      </div>
      <p v-if="rangeLabel" class="report-view__range-hint">
        Showing data for <strong>{{ rangeLabel }}</strong>
      </p>
    </a-card>

    <a-row v-if="hasData" :gutter="[16, 16]" class="report-view__stats">
      <a-col :xs="24" :sm="8">
        <div class="report-view__stat">
          <span class="report-view__stat-label">Days</span>
          <span class="report-view__stat-value">{{ rowCount }}</span>
        </div>
      </a-col>
      <a-col :xs="24" :sm="8">
        <div class="report-view__stat">
          <span class="report-view__stat-label">Events</span>
          <span class="report-view__stat-value">{{ eventCount }}</span>
        </div>
      </a-col>
      <a-col :xs="24" :sm="8">
        <div class="report-view__stat">
          <span class="report-view__stat-label">Total users</span>
          <span class="report-view__stat-value">{{ totalUsers }}</span>
        </div>
      </a-col>
    </a-row>

    <a-card :bordered="false" class="console-card report-view__table-card">
      <template #title>
        <span>Daily counts by event</span>
        <a-tag v-if="hasData" color="green" class="report-view__table-tag">
          {{ rowCount }} {{ rowCount === 1 ? "day" : "days" }}
        </a-tag>
      </template>
      <a-table
        v-if="hasData || confirmLoading"
        :columns="tableColumns"
        :data-source="transformedData"
        :row-key="getRowKey"
        :loading="confirmLoading"
        :pagination="tablePagination"
        :scroll="{ x: tableScrollX }"
        bordered
        size="middle"
        class="report-view__table"
      />
      <a-empty v-else class="report-view__empty">
        <template slot="description">
          <span class="report-view__empty-text">
            Pick a date range above to load event counts, or use
            <strong>Last 7 days</strong> / <strong>Last 30 days</strong>.
          </span>
        </template>
      </a-empty>
    </a-card>
  </div>
</template>

<script>
import moment from "moment";
import { utils, writeFileXLSX } from "xlsx";
import { sendRequest } from "@/http/axios.method";

export default {
  data() {
    return {
      config: {
        Authorization: "Bearer " + this.$store.state.authUser.token,
      },
      confirmLoading: false,
      dataSource: [],
      eventNames: [],
      fromDate: null,
      toDate: null,
      dateRange: null,
      transformedData: [],
    };
  },
  computed: {
    hasData() {
      return this.transformedData.length > 0;
    },
    canExport() {
      return this.hasData && !this.confirmLoading;
    },
    rowCount() {
      return this.transformedData.length;
    },
    eventCount() {
      return this.eventNames.length;
    },
    totalUsers() {
      let sum = 0;
      this.transformedData.forEach((row) => {
        this.eventNames.forEach((name) => {
          sum += Number(row[name]) || 0;
        });
      });
      return sum.toLocaleString();
    },
    rangeLabel() {
      if (this.fromDate && this.toDate) {
        return `${this.fromDate} — ${this.toDate}`;
      }
      if (!this.fromDate && !this.toDate) {
        return "all available dates";
      }
      return "";
    },
    tablePagination() {
      return {
        pageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["10", "15", "25", "50"],
        showTotal: (total) => `${total} days`,
      };
    },
    tableScrollX() {
      const base = 140;
      const perEvent = 112;
      return Math.max(640, base + this.eventNames.length * perEvent);
    },
    tableColumns() {
      const countRenderer = (text) => {
        const value = Number(text) || 0;
        const cls =
          value > 0
            ? "report-view__cell report-view__cell--active"
            : "report-view__cell report-view__cell--zero";
        return {
          children: this.$createElement("span", { class: cls }, value.toLocaleString()),
        };
      };

      return [
        {
          title: "Date",
          dataIndex: "date",
          key: "date",
          fixed: "left",
          width: 128,
          sorter: (a, b) => String(a.date).localeCompare(String(b.date)),
          defaultSortOrder: "descend",
          className: "report-view__col-date",
        },
        ...this.eventNames.map((eventName) => ({
          title: eventName,
          dataIndex: eventName,
          key: eventName,
          align: "right",
          width: 112,
          sorter: (a, b) => (Number(a[eventName]) || 0) - (Number(b[eventName]) || 0),
          customRender: countRenderer,
          className: "report-view__col-event",
        })),
        {
          title: "Total",
          key: "rowTotal",
          align: "right",
          width: 96,
          fixed: "right",
          sorter: (a, b) => this.rowTotal(a) - this.rowTotal(b),
          customRender: (text, row) => {
            const value = this.rowTotal(row);
            return {
              children: this.$createElement(
                "span",
                { class: "report-view__cell report-view__cell--total" },
                value.toLocaleString()
              ),
            };
          },
        },
      ];
    },
  },
  mounted() {
    this.dashboardReport();
  },
  methods: {
    applyPreset(days) {
      const end = moment().endOf("day");
      const start = moment().subtract(days - 1, "days").startOf("day");
      this.dateRange = [start, end];
      this.fromDate = start.format("YYYY-MM-DD");
      this.toDate = end.format("YYYY-MM-DD");
      this.dashboardReport();
    },
    handleDateChange(dates) {
      if (!dates || !dates.length || !dates[0] || !dates[1]) {
        this.fromDate = null;
        this.toDate = null;
        this.dateRange = null;
        this.dataSource = [];
        this.eventNames = [];
        this.transformedData = [];
        return;
      }
      const [fromDate, toDate] = dates;
      this.fromDate = fromDate.format("YYYY-MM-DD");
      this.toDate = toDate.format("YYYY-MM-DD");
      this.dashboardReport();
    },
    rowTotal(row) {
      return this.eventNames.reduce(
        (sum, name) => sum + (Number(row[name]) || 0),
        0
      );
    },
    dashboardReport() {
      this.confirmLoading = true;
      const from_date = this.fromDate || "";
      const to_date = this.toDate || "";
      const form_data = new FormData();

      form_data.append("from_date", from_date);
      form_data.append("to_date", to_date);
      sendRequest("post", "admin/get-event-date-user", form_data, this.config)
        .then((response) => {
          if (response.success) {
            const data = response.data || [];
            this.dataSource = data;
            const uniqueDates = [
              ...new Set(this.dataSource.map((item) => item.date)),
            ].sort((a, b) => String(b).localeCompare(String(a)));
            this.eventNames = [
              ...new Set(this.dataSource.map((item) => item.event_name)),
            ].sort((a, b) => String(a).localeCompare(String(b)));

            this.transformedData = this.transformDataSource(
              uniqueDates,
              this.eventNames
            );
          } else {
            if (response.status_code === 401) {
              this.$store.dispatch("logout");
              this.$router.push("/login");
            }
            if (response.status_code === 500) {
              this.dataSource = [];
              this.eventNames = [];
              this.transformedData = [];
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    transformDataSource(uniqueDates, uniqueEventNames) {
      return uniqueDates.map((date) => {
        const eventData = { date };

        uniqueEventNames.forEach((eventName) => {
          const event = this.dataSource.find(
            (item) => item.event_name === eventName && item.date === date
          );
          eventData[eventName] = event ? event.count : 0;
        });

        return eventData;
      });
    },
    getRowKey(record) {
      return record.date;
    },
    downloadExcel() {
      if (!this.canExport) return;

      const rows = this.transformedData.map((row) => {
        const out = { Date: row.date };
        this.eventNames.forEach((name) => {
          out[name] = row[name] ?? 0;
        });
        out.Total = this.rowTotal(row);
        return out;
      });

      const worksheet = utils.json_to_sheet(rows);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Event-wise users");
      const filename = this.fromDate && this.toDate
        ? `event-wise-users_${this.fromDate}_${this.toDate}.xlsx`
        : "event-wise-users.xlsx";
      writeFileXLSX(workbook, filename);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/base/variables";

.report-view__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.report-view__toolbar-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  flex: 1;
  min-width: 0;
}

.report-view__toolbar-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: $fw-semibold;
  color: $color-gray-9;
  font-size: 14px;
  flex-shrink: 0;
}

.report-view__toolbar-label .anticon {
  color: $color-primary;
  font-size: 16px;
}

.report-view__range-picker {
  min-width: 260px;
}

.report-view__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-view__range-hint {
  margin: 16px 0 0;
  padding-top: 16px;
  border-top: 1px solid $color-gray-4;
  font-size: 13px;
  color: $color-muted;
  line-height: 1.5;

  strong {
    color: $color-gray-9;
    font-weight: $fw-semibold;
  }
}

.report-view__stats {
  margin-bottom: 20px;
}

.report-view__stat {
  background: $color-gray-1;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: $shadow-2;
  height: 100%;
}

.report-view__stat-label {
  display: block;
  font-size: 12px;
  font-weight: $fw-semibold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $color-muted;
  margin-bottom: 6px;
}

.report-view__stat-value {
  display: block;
  font-size: 26px;
  font-weight: $fw-bold;
  color: $color-gray-10;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.report-view__table-card ::v-deep .ant-card-head-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.report-view__table-tag {
  margin: 0;
  font-weight: $fw-semibold;
}

.report-view__table ::v-deep .ant-table-thead > tr > th {
  background: $color-gray-2;
  font-weight: $fw-semibold;
  font-size: 13px;
  white-space: nowrap;
}

.report-view__table ::v-deep .report-view__col-date {
  background: $color-gray-2;
  font-weight: $fw-semibold;
}

.report-view__table ::v-deep .ant-table-tbody > tr:hover > td {
  background: rgba($color-primary, 0.04);
}

.report-view__table ::v-deep .report-view__cell {
  font-variant-numeric: tabular-nums;
  font-weight: $fw-semibold;
}

.report-view__table ::v-deep .report-view__cell--zero {
  color: $color-gray-6;
  font-weight: $fw-regular;
}

.report-view__table ::v-deep .report-view__cell--active {
  color: $color-gray-10;
}

.report-view__table ::v-deep .report-view__cell--total {
  color: $color-primary;
  font-weight: $fw-bold;
}

.report-view__empty {
  padding: 48px 16px;
}

.report-view__empty-text {
  display: block;
  max-width: 360px;
  margin: 0 auto;
  color: $color-muted;
  line-height: 1.6;

  strong {
    color: $color-gray-9;
  }
}

.report-view ::v-deep .ant-card:last-child {
  margin-bottom: 0;
}
</style>
